/**
 * RBAC vocabulary: application roles, workspace roles and scopes.
 *
 * Scopes use the `[resource]:[action]` format. `*` is the owner/admin wildcard
 * and is intentionally only granted to `workspace_owner` / `workspace_admin`.
 * The same tables are mirrored into Postgres (`role_scopes`) so RLS and the API
 * agree on every check.
 */

export const APP_ROLES = {
  superuser: "superuser",
  user: "user",
} as const;

export type AppRole = (typeof APP_ROLES)[keyof typeof APP_ROLES];

export const DEFAULT_APP_ROLE: AppRole = APP_ROLES.user;

export const WORKSPACE_ROLES = {
  owner: "workspace_owner",
  admin: "workspace_admin",
  posAdmin: "pos_admin",
  productAdmin: "product_admin",
  ordersAdmin: "orders_admin",
  logsAdmin: "logs_admin",
  usersAdmin: "users_admin",
} as const;

export type WorkspaceRole = (typeof WORKSPACE_ROLES)[keyof typeof WORKSPACE_ROLES];

export const ALL_WORKSPACE_ROLES: readonly WorkspaceRole[] = [
  WORKSPACE_ROLES.owner,
  WORKSPACE_ROLES.admin,
  WORKSPACE_ROLES.posAdmin,
  WORKSPACE_ROLES.productAdmin,
  WORKSPACE_ROLES.ordersAdmin,
  WORKSPACE_ROLES.logsAdmin,
  WORKSPACE_ROLES.usersAdmin,
];

export const OWNER_ROLE: WorkspaceRole = WORKSPACE_ROLES.owner;

/** Owner is intentionally excluded: ownership is transferred, never assigned. */
export const ASSIGNABLE_WORKSPACE_ROLES: readonly WorkspaceRole[] = [
  WORKSPACE_ROLES.admin,
  WORKSPACE_ROLES.posAdmin,
  WORKSPACE_ROLES.productAdmin,
  WORKSPACE_ROLES.ordersAdmin,
  WORKSPACE_ROLES.logsAdmin,
  WORKSPACE_ROLES.usersAdmin,
];

/** Tuple form so `z.enum` keeps the literal union instead of widening to string. */
export const ASSIGNABLE_WORKSPACE_ROLE_VALUES = [
  WORKSPACE_ROLES.admin,
  WORKSPACE_ROLES.posAdmin,
  WORKSPACE_ROLES.productAdmin,
  WORKSPACE_ROLES.ordersAdmin,
  WORKSPACE_ROLES.logsAdmin,
  WORKSPACE_ROLES.usersAdmin,
] as const;

export const MEMBER_STATUSES = {
  invited: "invited",
  active: "active",
  suspended: "suspended",
} as const;

export type MemberStatus = (typeof MEMBER_STATUSES)[keyof typeof MEMBER_STATUSES];

export const ROLE_LABELS: Record<WorkspaceRole, string> = {
  workspace_owner: "Workspace Owner",
  workspace_admin: "Workspace Admin",
  pos_admin: "POS Admin",
  product_admin: "Product Admin",
  orders_admin: "Orders Admin",
  logs_admin: "Logs Admin",
  users_admin: "Users Admin",
};

export const ROLE_DESCRIPTIONS: Record<WorkspaceRole, string> = {
  workspace_owner: "Full control of the workspace. Cannot be revoked or reassigned by anyone.",
  workspace_admin: "Every scope except transferring or revoking ownership.",
  pos_admin: "Checkout desk: run the POS, read products, create and read orders.",
  product_admin: "Catalog and inventory control.",
  orders_admin: "Order lifecycle, payments and invoices.",
  logs_admin: "Read-only access to the workspace audit trail.",
  users_admin: "Invite, suspend and re-role workspace members.",
};

/* ------------------------------------------------------------------- scopes */

export const SCOPE_RESOURCES = {
  pos: "pos",
  products: "products",
  orders: "orders",
  logs: "logs",
  users: "users",
  settings: "settings",
  reports: "reports",
  storage: "storage",
} as const;

export type ScopeResource = (typeof SCOPE_RESOURCES)[keyof typeof SCOPE_RESOURCES];

export const SCOPE_ACTIONS = {
  create: "create",
  read: "read",
  update: "update",
  delete: "delete",
  write: "write",
} as const;

export type ScopeAction = (typeof SCOPE_ACTIONS)[keyof typeof SCOPE_ACTIONS];

export const SCOPE_SEPARATOR = ":" as const;
export const SCOPE_WILDCARD = "*" as const;

export const SCOPES = {
  posAll: "pos:*",
  productsAll: "products:*",
  productsRead: "products:read",
  productsCreate: "products:create",
  productsUpdate: "products:update",
  productsDelete: "products:delete",
  ordersAll: "orders:*",
  ordersRead: "orders:read",
  ordersCreate: "orders:create",
  ordersUpdate: "orders:update",
  ordersDelete: "orders:delete",
  logsRead: "logs:read",
  usersAll: "users:*",
  usersRead: "users:read",
  usersCreate: "users:create",
  usersUpdate: "users:update",
  usersDelete: "users:delete",
  settingsAll: "settings:*",
  settingsRead: "settings:read",
  settingsUpdate: "settings:update",
  reportsRead: "reports:read",
  storageRead: "storage:read",
  storageWrite: "storage:write",
} as const;

export type Scope = (typeof SCOPES)[keyof typeof SCOPES] | (string & {});

export const ALL_SCOPES = [SCOPE_WILDCARD] as const;

export const ROLE_SCOPES: Record<WorkspaceRole, readonly Scope[]> = {
  workspace_owner: ALL_SCOPES,
  workspace_admin: ALL_SCOPES,
  pos_admin: [
    SCOPES.posAll,
    SCOPES.productsRead,
    SCOPES.ordersCreate,
    SCOPES.ordersRead,
    SCOPES.reportsRead,
    SCOPES.storageWrite,
  ],
  product_admin: [SCOPES.productsAll, SCOPES.storageWrite],
  orders_admin: [
    SCOPES.ordersAll,
    SCOPES.productsRead,
    SCOPES.reportsRead,
    SCOPES.storageWrite,
  ],
  logs_admin: [SCOPES.logsRead],
  users_admin: [SCOPES.usersAll],
};

/** Roles that hold `*` and therefore bypass per-scope checks. */
export const ELEVATED_WORKSPACE_ROLES: readonly WorkspaceRole[] = [
  WORKSPACE_ROLES.owner,
  WORKSPACE_ROLES.admin,
];

export const WORKSPACE_SLUG_PARAM = "workspaceSlug" as const;
